/**
 * `EndpointDetailModal` — the click-to-open detail for a single ordinary
 * (non-freshness) endpoint row: the endpoint writ large, its live URL,
 * uptime % for the page window, status counts, and the per-bucket history
 * bar. Freshness endpoints keep their richer `LagDetailModal`.
 *
 * Exists primarily so EVERY endpoint has a shareable
 * `?service=…&lagEndpoint=…` deep link (the address bar updates on open —
 * copy it, and the OG preview backend renders a matching card).
 *
 * The history bar is injected as a ReactNode: `StatusHistoryBar` lives
 * module-private in App.tsx, and the caller already has the exact props.
 */
import { useRef, type ReactNode } from 'react';
import clsx from 'clsx';
import { bucketSeverity, type BucketSeverity } from '../lib/bucket-severity';
import {
  formatEndpointUrl,
  isSyntheticEndpoint,
  parseEndpointKey,
} from '../lib/endpoint-key';
import { formatPct } from '../lib/status-mapping';
import type { EndpointSummary } from '../lib/status-types';
import { ModalCloseButton, ModalShell } from './ModalShell';

const TONE_DOT: Record<BucketSeverity, string> = {
  ok: 'bg-ok shadow-[0_0_0_3px_rgba(26,166,107,.18)]',
  warn: 'bg-warn shadow-[0_0_0_3px_rgba(224,160,25,.2)]',
  major: 'bg-major shadow-[0_0_0_3px_rgba(242,135,47,.2)]',
  down: 'bg-down shadow-[0_0_0_3px_rgba(227,80,62,.2)]',
  no_data: 'bg-line-strong',
};

/** `'1h'` → `'last hour'` — window wording for the uptime caption. */
export function windowCaption(windowSel: '1h' | '24h' | '90d'): string {
  if (windowSel === '1h') return 'last hour';
  if (windowSel === '24h') return 'last 24 hours';
  return 'last 90 days';
}

/** Terse status-count summary, omitting zero buckets: `1438 ok · 2 down`. */
export function countsSummary(
  ep: Pick<EndpointSummary, 'okCount' | 'warnCount' | 'downCount'>,
): string {
  const parts: string[] = [];
  if (ep.okCount > 0) parts.push(`${ep.okCount} ok`);
  if (ep.warnCount > 0) parts.push(`${ep.warnCount} warn`);
  if (ep.downCount > 0) parts.push(`${ep.downCount} down`);
  return parts.length > 0 ? parts.join(' · ') : 'no checks in window';
}

export interface EndpointDetailModalProps {
  ep: EndpointSummary;
  network: 'mainnet' | 'testnet';
  /** Owning suite's display name, e.g. `API v3`. */
  serviceName: string;
  windowSel: '1h' | '24h' | '90d';
  /** Pre-rendered `StatusHistoryBar` for this endpoint (or null). */
  historyBar: ReactNode;
  onClose: () => void;
}

export function EndpointDetailModal({
  ep,
  network,
  serviceName,
  windowSel,
  historyBar,
  onClose,
}: EndpointDetailModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const parsed = parseEndpointKey(ep.endpointKey);
  const url = formatEndpointUrl({ ...parsed, network });
  const showMethod = parsed.method && !isSyntheticEndpoint(ep.endpointKey);

  return (
    <ModalShell
      onClose={onClose}
      labelledById="endpoint-detail-title"
      maxWidthClass="max-w-lg"
      initialFocusRef={closeRef}
    >
      <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3.5 sm:px-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <span
              className={clsx(
                'h-2.5 w-2.5 shrink-0 rounded-full',
                TONE_DOT[bucketSeverity(ep, windowSel)],
              )}
              aria-hidden
            />
            {showMethod && (
              <span className="rounded border border-line bg-paper-2 px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tracking-wide text-ink-2">
                {parsed.method}
              </span>
            )}
            <h2
              id="endpoint-detail-title"
              className="truncate font-mono text-[15px] font-medium tracking-[-.01em] text-ink-1"
            >
              {parsed.path}
            </h2>
          </div>
          <div className="mt-1 text-[12.5px] text-ink-3">
            {serviceName} · {network}
          </div>
        </div>
        <ModalCloseButton onClose={onClose} buttonRef={closeRef} />
      </div>

      <div className="overflow-y-auto px-4 py-4 sm:px-5">
        {url && (
          <div className="truncate font-mono text-[12px] tracking-[-.01em] text-ink-3">
            {url}
          </div>
        )}
        <div className="mt-3 flex items-baseline gap-2.5">
          <div className="text-[34px] font-semibold leading-none text-ink-1 [font-feature-settings:'tnum']">
            {formatPct(ep.successPct)}
          </div>
          <div className="text-[13px] text-ink-2">
            uptime · {windowCaption(windowSel)}
          </div>
        </div>
        <div className="mt-1.5 text-[12.5px] text-ink-3">
          {countsSummary(ep)}
        </div>
        {historyBar !== null && <div className="mt-4">{historyBar}</div>}
      </div>
    </ModalShell>
  );
}
